import { useCallback, useEffect, useRef } from 'react'

/**
 * useDocumentVisibility
 *
 * React hook to track document (tab/window) visibility and queue actions for execution when the document becomes visible again.
 *
 * - `isDocumentVisible`: Ref<boolean> indicating if the document is currently visible (true) or hidden (false).
 * - `addToExecutionQueue(fn)`: Function to queue a callback for execution the next time the document becomes visible. Useful for deferring side effects (e.g., marking messages as read) until the user returns to the tab.
 *
 * Usage:
 * ```ts
 * const { isDocumentVisible, addToExecutionQueue } = useDocumentVisibility();
 *
 * if (shouldDoSomething) {
 *   if (isDocumentVisible.current) {
 *     doSomething();
 *   } else {
 *     addToExecutionQueue(doSomething);
 *   }
 * }
 * ```
 *
 * The queue is flushed automatically when the document becomes visible.
 */
export const useDocumentVisibility = () => {
  const isDocumentVisible = useRef(true)

  const executionQueue = useRef<(() => void)[]>([])

  const addToExecutionQueue = useCallback((fn: () => void) => {
    executionQueue.current.push(fn)
  }, [])

  const executeExecutionQueue = useCallback(() => {
    executionQueue.current.forEach((fn) => fn())
    executionQueue.current = []
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      isDocumentVisible.current = document.visibilityState === 'visible'

      if (isDocumentVisible.current) {
        executeExecutionQueue()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [executeExecutionQueue])

  return { isDocumentVisible, addToExecutionQueue }
}
